"use client";

import { useAccountStats } from "@/hooks/useAccountStats";
import { useMemo } from "react";
import { useDonations } from "@/hooks/useDonations";
import { useTopDonors } from "@/hooks/useTopDonors";
import { endOfMonth, startOfMonth } from "date-fns";
import { DonationsChart } from "@/components/DonationsChart";
import { HomeStatsCards } from "@/containers/home/HomeStatsCards";
import { HomeTopDonorsCard } from "@/containers/home/HomeTopDonorsCard";
import { HomeLatestDonationsCard } from "@/containers/home/HomeLatestDonationsCard";
import { RecurringDonorTables } from "@/containers/recurring/RecurringDonorTables";

export default function Home() {
  const { accountStats, isLoading } = useAccountStats({});
  const range = useMemo(() => {
    return {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    };
  }, []);
  const { accountStats: monthStats } = useAccountStats(range);

  const { donations, isLoading: donationsLoading } = useDonations({
    initialPage: 0,
    limit: 6,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const { topDonors, isLoading: topDonorsLoading } = useTopDonors(10);

  const recurringRevenue = useMemo(() => {
    if (!accountStats) return 0;
    const monthly =
      accountStats.plans?.find((p) => p.interval === "monthly")?._sum
        .grossAmount ?? 0;
    const weekly =
      accountStats.plans?.find((p) => p.interval === "weekly")?._sum
        .grossAmount ?? 0;
    const revenue = (monthly + 4 * weekly) / 100;
    return revenue;
  }, [accountStats]);

  const monthRecurringRevenue = useMemo(() => {
    if (!monthStats) return 0;
    const monthly =
      monthStats.plans?.find((p) => p.interval === "monthly")?._sum
        .grossAmount ?? 0;
    const weekly =
      monthStats.plans?.find((p) => p.interval === "weekly")?._sum
        .grossAmount ?? 0;
    const revenue = (monthly + 4 * weekly) / 100;
    return revenue;
  }, [monthStats]);

  const monthRaisedCents = monthStats?.raised ?? 0;
  const monthDonorsDelta = monthStats?.donors ?? 0;

  const donationOverviewFilters = useMemo(() => ({ status: "paid" }), []);

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-4 sm:px-6">
      <HomeStatsCards
        isLoading={isLoading}
        raisedCents={accountStats?.raised ?? 0}
        monthRaisedCents={monthRaisedCents}
        donors={accountStats?.donors ?? 0}
        monthDonorsDelta={monthDonorsDelta}
        recurringRevenue={recurringRevenue}
        monthRecurringRevenue={monthRecurringRevenue}
      />

      <DonationsChart filters={donationOverviewFilters} />

      <HomeLatestDonationsCard
        donations={donations}
        isLoading={donationsLoading}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <HomeTopDonorsCard
          topDonors={topDonors}
          isLoading={topDonorsLoading}
        />
        <RecurringDonorTables />
      </div>
    </div>
  );
}
